// Serialization contract for persisted expected-action annotations.
//
// A file's expected actions are stored as ONE JSON payload (see the Datasets
// annotation save flow). The payload shape is intentionally stable and
// self-contained — it does not depend on the reference Labeling Studio
// (apps/ai.rpe.app). Parsing is defensive because the JSON originates from a
// downloaded blob and may be malformed or produced by an older writer.

import type {
  EngagementEntry,
  ExpectedActionDraft,
  InteractionType,
} from './expectedAction.interface.js';

/** Current on-disk payload schema version. */
export const EXPECTED_ACTION_PAYLOAD_VERSION = 1 as const;

/**
 * One persisted expected action: the editable draft plus the timeline anchor
 * (which utterance it hangs off, and its position in the event stream).
 */
export interface SavedExpectedAction {
  turnId: string;
  afterEventIndex: number;
  draft: ExpectedActionDraft;
}

/**
 * Who produced an annotation payload. Captured so saves are attributable even
 * though the dataset-level RBAC (not this field) is what authorizes the write.
 */
export interface LabeledBy {
  /** Entra object id (OID) of the labeler. */
  id: string;
  /** Human-readable alias/UPN of the labeler, when known. */
  alias?: string;
}

/** The full JSON document written per annotated file. */
export interface ExpectedActionPayload {
  version: number;
  savedAt: string;
  /** The raw data file these annotations describe, when known. */
  fileId?: string;
  /**
   * Provenance: the raw data file id these annotations describe. Mirrors
   * `fileId` (retained for the storage key / backward compatibility) but is the
   * explicit source-of-truth field going forward.
   */
  sourceFileId?: string;
  /**
   * Hash of the annotated source content at save time. Lets a reader detect
   * drift (the underlying file changed since it was annotated) without
   * snapshotting the full content into this sidecar.
   */
  sourceContentHash?: string;
  /** Who saved this payload; for attribution, not authorization. */
  labeledBy?: LabeledBy;
  actions: SavedExpectedAction[];
}

const INTERACTION_TYPES: readonly InteractionType[] = ['reactive', 'proactive'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function toInteractionTypes(value: unknown): InteractionType[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is InteractionType =>
    INTERACTION_TYPES.includes(item as InteractionType),
  );
}

function toEngagementInputs(value: unknown): EngagementEntry[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): EngagementEntry[] => {
    if (!isRecord(item)) return [];
    const type = item.type;
    if (typeof type !== 'string') return [];
    const inputs: Record<string, string> = {};
    if (isRecord(item.inputs)) {
      for (const [key, raw] of Object.entries(item.inputs)) {
        if (typeof raw === 'string') inputs[key] = raw;
      }
    }
    return [{ type, inputs }];
  });
}

function toDraft(value: unknown): ExpectedActionDraft {
  const source = isRecord(value) ? value : {};
  return {
    engagementInputs: toEngagementInputs(source.engagementInputs),
    interactionTypes: toInteractionTypes(source.interactionTypes),
    associatedScenarios: toStringArray(source.associatedScenarios),
    associatedCustomizationAsks: toStringArray(
      source.associatedCustomizationAsks,
    ),
  };
}

function toSavedAction(value: unknown): SavedExpectedAction | null {
  if (!isRecord(value)) return null;
  if (typeof value.turnId !== 'string') return null;
  const afterEventIndex =
    typeof value.afterEventIndex === 'number' ? value.afterEventIndex : 0;
  return {
    turnId: value.turnId,
    afterEventIndex,
    draft: toDraft(value.draft),
  };
}

/**
 * Serialize a file's expected actions to the canonical JSON string that gets
 * uploaded as the annotation payload.
 */
export function serializeExpectedActions(input: {
  actions: SavedExpectedAction[];
  fileId?: string;
  savedAt?: string;
  sourceContentHash?: string;
  labeledBy?: LabeledBy;
}): string {
  const payload: ExpectedActionPayload = {
    version: EXPECTED_ACTION_PAYLOAD_VERSION,
    savedAt: input.savedAt ?? new Date().toISOString(),
    ...(input.fileId !== undefined
      ? { fileId: input.fileId, sourceFileId: input.fileId }
      : {}),
    ...(input.sourceContentHash !== undefined
      ? { sourceContentHash: input.sourceContentHash }
      : {}),
    ...(input.labeledBy !== undefined ? { labeledBy: input.labeledBy } : {}),
    actions: input.actions.map((action) => ({
      turnId: action.turnId,
      afterEventIndex: action.afterEventIndex,
      draft: action.draft,
    })),
  };
  return JSON.stringify(payload, null, 2);
}

/**
 * Compute a stable SHA-256 hex digest of the given text. Used to stamp an
 * annotated file's source content hash so readers can detect drift. Returns an
 * empty string if the platform lacks Web Crypto (never throws).
 */
export async function computeContentHash(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) return '';
  const bytes = new TextEncoder().encode(text);
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Parse a previously saved payload back into saved actions. Returns an empty
 * array when the JSON is missing, malformed, or contains no valid actions, so
 * re-opening an unreadable file degrades to a blank editor rather than
 * throwing.
 */
export function parseExpectedActions(json: string): SavedExpectedAction[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.actions)) return [];
  return parsed.actions.flatMap((action) => {
    const saved = toSavedAction(action);
    return saved ? [saved] : [];
  });
}

/** Authoring must never turn an unreadable file into a blank editable document. */
export function parseExpectedActionsStrict(
  json: string,
  sourceFileId?: string,
): SavedExpectedAction[] {
  const value: unknown = JSON.parse(json);
  if (
    !isRecord(value) ||
    value.version !== EXPECTED_ACTION_PAYLOAD_VERSION ||
    !Array.isArray(value.actions)
  ) {
    throw new Error('Unsupported or malformed annotation document.');
  }
  if (
    sourceFileId !== undefined &&
    [value.fileId, value.sourceFileId].some(
      (id) => id !== undefined && id !== sourceFileId,
    )
  ) {
    throw new Error(
      'The annotation payload belongs to a different source file.',
    );
  }
  for (const action of value.actions) {
    if (
      !isRecord(action) ||
      typeof action.turnId !== 'string' ||
      !Number.isSafeInteger(action.afterEventIndex) ||
      Number(action.afterEventIndex) < 0 ||
      !isRecord(action.draft) ||
      !Array.isArray(action.draft.engagementInputs) ||
      !action.draft.engagementInputs.every(
        (entry: unknown) =>
          isRecord(entry) &&
          typeof entry.type === 'string' &&
          isRecord(entry.inputs) &&
          Object.values(entry.inputs).every(
            (input) => typeof input === 'string',
          ),
      ) ||
      !Array.isArray(action.draft.interactionTypes) ||
      !action.draft.interactionTypes.every(
        (type: unknown) => type === 'reactive' || type === 'proactive',
      ) ||
      !Array.isArray(action.draft.associatedScenarios) ||
      !action.draft.associatedScenarios.every(
        (item: unknown) => typeof item === 'string',
      ) ||
      !Array.isArray(action.draft.associatedCustomizationAsks) ||
      !action.draft.associatedCustomizationAsks.every(
        (item: unknown) => typeof item === 'string',
      )
    ) {
      throw new Error(
        'An annotation action is malformed. The original file has not been changed.',
      );
    }
  }
  return parseExpectedActions(json);
}
